import { finalizeOrder } from 'modules/order/finalize-order/finalize-order-thunk'
import { Frequency } from 'models/RecurringDonationPlan'
import {
  checkCanMakePayment,
  checkWalletType,
} from 'modules/order/state/stripe-payment-request/stripe-payment-request-slice'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { setTransactionId, setTransactionToken } from 'redux/features/transaction'
import { RootState, TypedDispatch } from 'redux/store'
import { trackPurchaseEvents } from '../../analytics/helpers/track-purchase-analytics-event'
import { Organization } from '../../../models/Organization'
import { FinalizeOrderArgs, CampaignForFinalization } from './utils/types'
import { StatusType } from 'modules/status/state/status-types'
import statusSlice from 'modules/status/state/status-slice'
import { PurchaseCookieDto } from '../../../models/Purchase'
import { StructuredError } from 'types/error'

interface OrderDetails {
  organization: Organization
  campaign: CampaignForFinalization
  c_src: string | undefined
  c_src2: string | undefined
  referrer: string
  currency: string
  baseAmount: number
  frequency: Frequency
  recurringEndDate: FinalizeOrderArgs['recurringEndDate']
  utmParams: Record<string, string>
  reCaptchaToken?: string
}

interface ProcessFinalizeOrderResult {
  error?: string | StructuredError
  redirectUrl?: string
  transactionId?: number
  purchaseCookieData?: PurchaseCookieDto
}

export const processFinalizeOrder = async (
  orderDetails: OrderDetails,
  dispatch: TypedDispatch<RootState>,
): Promise<ProcessFinalizeOrderResult> => {
  const result = await dispatch(finalizeOrder(orderDetails))

  if (finalizeOrder.rejected.match(result)) {
    let error: string | StructuredError = 'Unknown error occurred while finalizing order.'

    if (typeof result.payload === 'string') {
      error = result.payload
    } else if (result.payload instanceof StructuredError) {
      // StructuredError is already the right type
      error = result.payload
    } else if (result.payload instanceof Error) {
      error = result.payload.message
    } else if (
      result.payload &&
      typeof result.payload === 'object' &&
      'title' in result.payload &&
      'message' in result.payload
    ) {
      // Redux serialized StructuredError - recreate it
      error = new StructuredError(result.payload.title as string, result.payload.message as string)
    } else if (typeof result.payload === 'object' && result.payload !== null) {
      error = JSON.stringify(result.payload)
    } else if (result.error) {
      if (result.error instanceof StructuredError) {
        error = result.error
      } else if ('title' in result.error && 'message' in result.error) {
        // Redux serialized the StructuredError with our custom serializer, so recreate it
        error = new StructuredError(result.error.title as string, result.error.message as string)
      } else if (result.error.message) {
        error = result.error.message
      } else {
        error = String(result.error)
      }
    }

    return { error }
  }

  if (finalizeOrder.fulfilled.match(result)) {
    const analytics = getAnalyticsInstance()

    const { purchase, purchaser, transaction, purchaseCookieData } = result.payload
    const { campaign, organization } = orderDetails

    try {
      await analytics.identify(purchaser.member_id.toString(), {
        hostName: window.location.hostname,
        visitorType: 'member',
      })

      const dwLoaded = dispatch(checkCanMakePayment())
      const walletType = dispatch(checkWalletType())

      await trackPurchaseEvents({
        purchase,
        purchaser,
        campaign: campaign,
        organization: organization,
        dwLoaded,
        walletType,
      })
    } catch (e) {
      Bugsnag.notify(e as NotifiableError)
      console.warn(e)
    }

    if (transaction && 'id' in transaction.data) {
      const transactionId = Number(transaction.data.id)
      dispatch(setTransactionId(transactionId))
      dispatch(setTransactionToken(transaction.token))

      /**
       * Create cookie for thank you page. This the data contract between the two apps.
       * Whammy PurchaseCookieDto can be found in whammy/src/models/purchase.ts
       * Checkout PurchaseCookieDto can be found in src/models/Purchase.ts
       * Cookie must be encoded to escape special characters the cookie
       * spec doesn't support (semicolons)
       */
      const encodedValue = encodeURIComponent(JSON.stringify(purchaseCookieData))

      document.cookie = `purchase_${purchase.id}=${encodedValue}; Secure; expires=${new Date(
        Date.now() + 15 * 60 * 1000,
      ).toUTCString()}; path=/;`

      dispatch(statusSlice.actions.setStatus({ status: StatusType.COMPLETED }))

      return { transactionId, purchaseCookieData }
    }

    dispatch(statusSlice.actions.setStatus({ status: StatusType.COMPLETED }))
  }

  return {}
}

export default processFinalizeOrder
